import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchNotices } from '../utils/api';

/**
 * 공지사항 리스트 모달
 * @param {boolean} isOpen - 모달 열림 여부
 * @param {Function} onClose - 모달 닫기 함수
 */
function NoticeListModal({ isOpen, onClose }) {
  const { role } = useAuth();
  const [notices, setNotices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedNotice, setSelectedNotice] = useState(null);

  const loadNotices = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchNotices(role);
      setNotices(data || []);
    } catch (err) {
      console.error('공지사항 로드 오류:', err);
      setError('공지사항을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [role]);

  useEffect(() => {
    if (isOpen) {
      loadNotices();
    } else {
      setSelectedNotice(null);
    }
  }, [isOpen, loadNotices]);

  if (!isOpen) return null;

  // 중요도에 따른 스타일
  const priorityStyles = {
    high: 'border-red-500 bg-red-50',
    normal: 'border-orange-500 bg-orange-50',
    low: 'border-blue-500 bg-blue-50'
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      high: '중요',
      normal: '일반',
      low: '낮음'
    };
    return labels[priority] || '일반';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[300] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* 헤더 */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
            <h2 className="text-xl font-bold text-gray-800">공지사항</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            aria-label="닫기"
          >
            ×
          </button>
        </div>

        {/* 내용 */}
        <div className="flex-1 overflow-hidden flex">
          {/* 리스트 영역 */}
          <div className="w-1/3 border-r border-gray-200 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-2"></div>
                <p>로딩 중...</p>
              </div>
            ) : error ? (
              <div className="p-4 text-center text-red-500">
                <p>{error}</p>
                <button
                  onClick={loadNotices}
                  className="mt-2 px-3 py-1 bg-orange-500 text-white rounded text-sm hover:bg-orange-600"
                >
                  다시 시도
                </button>
              </div>
            ) : notices.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <p>공지사항이 없습니다.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {notices.map((notice) => (
                  <button
                    key={notice.id}
                    onClick={() => setSelectedNotice(notice)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                      selectedNotice?.id === notice.id ? 'bg-orange-50 border-l-4 border-orange-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-800 truncate">{notice.title}</h3>
                          {notice.priority === 'high' && (
                            <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs font-semibold rounded flex-shrink-0">
                              중요
                            </span>
                          )}
                        </div>
                        {notice.createdAt && (
                          <p className="text-xs text-gray-500">
                            {new Date(notice.createdAt).toLocaleDateString('ko-KR')}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 상세 영역 */}
          <div className="flex-1 overflow-y-auto">
            {selectedNotice ? (
              <div className={`p-6 border-l-2 ${priorityStyles[selectedNotice.priority] || priorityStyles.normal}`}>
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-bold text-gray-800">{selectedNotice.title}</h3>
                    <span className="px-2 py-1 bg-white rounded text-xs font-semibold">
                      {getPriorityLabel(selectedNotice.priority)}
                    </span>
                  </div>
                  {selectedNotice.createdAt && (
                    <p className="text-sm text-gray-500">
                      {new Date(selectedNotice.createdAt).toLocaleString('ko-KR')}
                    </p>
                  )}
                </div>
                <div className="text-gray-700 whitespace-pre-wrap leading-relaxed bg-white p-4 rounded">
                  {selectedNotice.content}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <p>공지사항을 선택하세요</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default NoticeListModal;
